import { useCallback, useEffect, useState } from "react";
import { Editor } from "./Editor";
import { Home } from "./Home";
import { createProject, type PhotoFolioProject, type TemplateId } from "../domain/project";
import { listProjects, saveProject } from "../storage/database";
import { importPhoto } from "../media/process";
import { importProjectArchive } from "../exporters/project-exporter";
import { DEMO_PHOTOS, type DemoCollection } from "./home-showcase-data";

const coverIndexByTemplate: Record<TemplateId, number> = {
  paper: 0,
  timeline: 1,
  gallery: 2,
  book: 1,
  cinema: 2,
  deck: 0,
  focus: 1,
  scrapbook: 2,
  accordion: 0,
  drift: 1,
  circular: 0,
  depth: 1,
};
let demoRun = 0;

export function App() {
  const [projects, setProjects] = useState<PhotoFolioProject[]>([]);
  const [active, setActive] = useState<PhotoFolioProject | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => setProjects(await listProjects()), []);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const openProject = async (project: PhotoFolioProject) => {
    const opened = { ...project, lastOpenedAt: new Date().toISOString() };
    await saveProject(opened);
    setActive(opened);
  };

  const createNew = async (details: { title: string; subtitle: string; date: string; location: string; people: string[] }) => {
    const project = { ...createProject(details.title), ...details };
    await saveProject(project);
    setProjects((current) => [project, ...current]);
    setActive(project);
  };

  const importDraft = async (file: File) => {
    try {
      const project = await importProjectArchive(file);
      await refresh();
      setActive(project);
    } catch (error) {
      window.alert(error instanceof Error ? error.message : "草稿导入失败");
    }
  };

  const createDemo = async (template: TemplateId = "paper", collection: DemoCollection = "landscape") => {
    const project = createProject(collection === "people" ? "人物相遇手记" : "风景夜航手记");
    project.template = template;
    project.subtitle = collection === "people"
      ? "三张人物照片，按相遇的顺序保存下来。"
      : "三张风景照片，一段从林间走到夜空的路。";
    project.location = collection === "people" ? "人物 / 本地示例" : "风景 / 本地示例";
    const run = demoRun++;
    await saveProject(project);
    try {
      const items = [];
      for (const [index, photo] of DEMO_PHOTOS[collection].entries()) {
        const response = await fetch(`${import.meta.env.BASE_URL}showcase/${photo.fileName}`);
        if (!response.ok) throw new Error("示例图片尚未准备好");
        const file = new File([await response.blob()], photo.fileName, { type: "image/webp" });
        const result = await importPhoto(project.id, file, index);
        result.item.title = photo.title;
        result.item.caption = photo.caption;
        result.item.location = photo.location;
        items.push(result.item);
      }
      const projectSeed = [...project.id].reduce((total, character) => total + character.charCodeAt(0), 0);
      const coverIndex = items.length ? (coverIndexByTemplate[template] + projectSeed + run) % items.length : 0;
      const completed = { ...project, items, coverAssetId: items[coverIndex]?.assetId, updatedAt: new Date().toISOString() };
      await saveProject(completed);
      await refresh();
      setActive(completed);
    } catch (error) {
      window.alert(error instanceof Error ? error.message : "示例创建失败");
    }
  };

  if (loading) return <div className="app-loading"><div /><span>正在打开本地影集</span></div>;

  return active ? (
    <Editor
      key={active.id}
      initialProject={active}
      onBack={() => { setActive(null); refresh(); }}
      onProjectChange={(project) => setProjects((current) => current.map((item) => item.id === project.id ? project : item))}
    />
  ) : <Home projects={projects} onCreate={createNew} onOpen={openProject} onImport={importDraft} onDemo={createDemo} />;
}
